/**
 * Georeferenced address as the backend returns it (`GeoReferenceApiModel`) — the working address of a valuation
 * and the result of an address lookup.
 *
 * Required are only the fields the GeoRef API documents as non-null: the precision, both display names and
 * `biggerArea`. Street, postcode and municipality are set for practically every address of street precision or
 * better, but the structure does not guarantee it — a coarser result (city, state or country precision)
 * legitimately omits them.
 *
 * The snake_case names are explicit `@JsonProperty`s on the backend fields, not a naming strategy — the remaining
 * fields really are camelCase.
 */

/** Precision of a georeferencing result; mirrors the backend enum `GeoRefPrecision`. */
export type GeoRefPrecision =
    | "COORDINATE"
    | "HOUSE"
    /** Hausnummer fehlt in den Adressdaten, Lage zwischen den Nachbar-Hausnummern derselben Straße interpoliert (#19748). */
    | "HOUSENUMBER_INTERPOLATED"
    | "STREET_SECTION"
    | "CITY_DISTRICT"
    | "POSTAL_CODE"
    | "CITY"
    | "STATE"
    | "COUNTRY";

/** Lage zwischen Nachbar-Hausnummern interpoliert (#19748) — REVA weist das in Adress-Tab und PDF aus. */
export function isHouseNumberInterpolated(address?: Pick<GeoRefAddress, "precision"> | null): boolean {
    return address?.precision === "HOUSENUMBER_INTERPOLATED";
}

export interface ILatLonCoordinate {
    lat: number;
    lon: number;
}

export interface GeoRefAddress extends ILatLonCoordinate {
    precision: GeoRefPrecision;
    displayNameDE: string;
    displayNameEN: string;
    street?: string;
    /**
     * Hausnummer des Auftrags (#19746). Bei hausgenauen Treffern steckt sie zusätzlich schon in `street`
     * („Hauptstr. 11“), bei straßenabschnittsgenauen nicht — dann ergänzt {@link buildStreetWithHouseNumber} sie.
     * Liefert nur reva-rest, nicht die GeoRef-API.
     */
    houseNumber?: string | null;
    /** Coarser locality label, e.g. "Wolfenbüttel 38300". */
    biggerArea: string;
    postcode?: string;
    cadastral_district?: string;
    /** Gemarkungsschlüssel des Flurstücks am Adresspunkt (#19384), Format je Bundesland verschieden. */
    cadastral_district_key?: string;
    /** Flurschlüssel des Flurstücks am Adresspunkt (#19384), Format je Bundesland verschieden; fehlt in Bayern und außerhalb von Flurstücken. */
    cadastral_key?: string;
    cadastral_parcel_number?: string;
    municipality?: string;
    municipality_code?: string;
    district?: string;
    district_code?: string;
    region?: string;
    region_code?: string;
    state?: string;
    state_code?: string;
}

/** Felder einer Adresse, aus denen die Adress-Parameter für die AVM gebaut werden. */
export interface AvmAddressFields extends ILatLonCoordinate {
    street?: string;
    houseNumber?: string | null;
    postcode?: string;
    municipality?: string;
}

/** Hausnummern-Token am Ende einer Straßenangabe („Römerstr. 24a“, „Hauptstr. 11-13“, „Weg 3/1“). */
const TRAILING_HOUSE_NUMBER = /\s(\d[\w/-]*)$/;

/** Führende Ziffernfolge einer Hausnummer („24a“ → „24“, „11-13“ → „11“). */
function leadingDigits(houseNumber: string): string {
    return houseNumber.match(/^\d+/)?.[0] ?? "";
}

/**
 * Straße mit Hausnummer (#19746): hängt `houseNumber` an `street` an, sofern die Straße nicht schon auf dieselbe
 * Hausnummer endet (hausgenaue Treffer liefern „Hauptstr. 11“ und zusätzlich houseNumber „11“ — keine
 * Doppelung; „Hauptstr. 11a“ + „11“ gilt ebenfalls als enthalten). Verglichen wird nur die führende Ziffernfolge,
 * damit Straßennamen mit Nummer („B 27“ + „5“ → „B 27 5“) die Hausnummer nicht verschlucken. Ohne Straße kommt
 * nie eine alleinstehende Hausnummer zurück, sondern ein leerer String.
 */
export function buildStreetWithHouseNumber(street?: string | null, houseNumber?: string | null): string {
    const streetTrimmed = (street ?? "").trim();
    const numberTrimmed = (houseNumber ?? "").trim();
    if (streetTrimmed === "" || numberTrimmed === "") return streetTrimmed;
    const trailing = streetTrimmed.match(TRAILING_HOUSE_NUMBER)?.[1];
    if (trailing !== undefined && leadingDigits(trailing) === leadingDigits(numberTrimmed)) return streetTrimmed;
    return `${streetTrimmed} ${numberTrimmed}`;
}

/**
 * Freitext-Adresse `<Straße Hausnummer>, <PLZ> <Ort>` (#19739, #19746). Fehlen Straße, PLZ und Ort, kommt ein
 * leerer String zurück.
 */
export function buildAddressClean(
    address: Pick<AvmAddressFields, "street" | "houseNumber" | "postcode" | "municipality">,
): string {
    const {street, houseNumber, postcode, municipality} = address;
    if (street === undefined && postcode === undefined && municipality === undefined) return "";
    return `${buildStreetWithHouseNumber(street, houseNumber)}, ${postcode ?? ""} ${municipality ?? ""}`;
}

/**
 * Adress-Parameter für alle AVM-Aufrufe: `<Straße>, <PLZ> <Ort> (<lat>/<lon>)` (#19739).
 *
 * Die AVM georeferenziert den Freitext und nutzt die Koordinate in Klammern als Hinweis: bei einem nicht
 * hausgenauen Treffer setzt sie den Punkt auf die Koordinate. REVA-Oberfläche und reva-pdf müssen daher
 * exakt dieselbe Form senden, sonst bewertet die AVM eine andere Stelle.
 *
 * Liegen weder Straße, PLZ noch Ort vor (z. B. reine Koordinatenauflösung), kommt nur `<lat>/<lon>` zurück.
 */
export function buildAvmAddressParam(address: AvmAddressFields): string {
    return buildAvmAddressParamFromClean(buildAddressClean(address), address);
}

/**
 * Wie {@link buildAvmAddressParam}, aber mit bereits gebauter Freitext-Adresse `addressClean` (#19739).
 * Ein leerer `addressClean` ergibt nur `<lat>/<lon>`.
 */
export function buildAvmAddressParamFromClean(addressClean: string, coordinate: ILatLonCoordinate): string {
    const latLon = `${coordinate.lat}/${coordinate.lon}`;
    return addressClean === "" ? latLon : `${addressClean} (${latLon})`;
}
